// client/src/components/Reports/SankeyChart.js
// CSS/MUI-based Sankey-style cash-flow visualization.
// recharts does not ship a Sankey component, so we build a clean
// proportional-bar flow table instead of pulling in a heavy dependency.
//
// Props:
//   nodes  – [{ id, name, type: 'income'|'category'|'merchant' }]
//   links  – [{ source, target, value }]
//   currency – string prefix e.g. '€' (default '€')

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';

// Colours per node type
const TYPE_COLORS = {
  income:   '#4caf50',
  category: '#1976d2',
  merchant: '#ff9800',
};

/**
 * Single proportional bar row showing one flow.
 */
function FlowRow({ sourceName, targetName, value, maxValue, sourceType, targetType, currency }) {
  const theme   = useTheme();
  const isDark  = theme.palette.mode === 'dark';
  const barPct  = maxValue > 0 ? Math.max(2, (value / maxValue) * 100) : 2;
  const srcColor = TYPE_COLORS[sourceType] || '#888';
  const tgtColor = TYPE_COLORS[targetType] || '#888';

  return (
    <Tooltip
      title={`${sourceName} → ${targetName}: ${currency}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
      placement="top"
      arrow
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 160px',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          px: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' },
          cursor: 'default',
        }}
      >
        {/* Source label */}
        <Typography
          variant="caption"
          noWrap
          sx={{ fontWeight: 600, color: srcColor, textAlign: 'right', pr: 1 }}
        >
          {sourceName}
        </Typography>

        {/* Flow bar */}
        <Box sx={{ position: 'relative', height: 14, borderRadius: 1, overflow: 'hidden', bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${barPct}%`,
              background: `linear-gradient(90deg, ${srcColor}cc, ${tgtColor}cc)`,
              borderRadius: 1,
              transition: 'width 0.4s ease',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 10,
              fontWeight: 700,
              color: isDark ? '#fff' : '#333',
              lineHeight: 1,
            }}
          >
            {currency}{Math.round(value).toLocaleString()}
          </Typography>
        </Box>

        {/* Target label */}
        <Typography
          variant="caption"
          noWrap
          sx={{ fontWeight: 600, color: tgtColor, pl: 1 }}
        >
          {targetName}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * SankeyChart
 *
 * Renders the top 10 flows by value as proportional bar rows grouped by
 * flow level (income→category and category→merchant).
 */
export default function SankeyChart({ nodes = [], links = [], currency = '€' }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Build node lookup map
  const nodeMap = useMemo(
    () => new Map(nodes.map(n => [n.id, n])),
    [nodes]
  );

  // Separate links by level and pick top 10 by value across all levels
  const { level1Links, level2Links } = useMemo(() => {
    const l1 = links
      .filter(l => {
        const src = nodeMap.get(l.source);
        return src && src.type === 'income';
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const l2 = links
      .filter(l => {
        const src = nodeMap.get(l.source);
        return src && src.type === 'category';
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    return { level1Links: l1, level2Links: l2 };
  }, [links, nodeMap]);

  const allShownLinks = [...level1Links, ...level2Links];
  const maxValue      = allShownLinks.length
    ? Math.max(...allShownLinks.map(l => l.value))
    : 1;

  if (!nodes.length || !links.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No cash-flow data available for this period.
        </Typography>
      </Box>
    );
  }

  const sectionHeader = (label) => (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        color: 'text.secondary',
        fontWeight: 700,
        letterSpacing: 1.5,
        mb: 0.5,
        mt: 1.5,
        px: 1,
      }}
    >
      {label}
    </Typography>
  );

  const renderLinks = (linkArr) =>
    linkArr.map((link, idx) => {
      const src = nodeMap.get(link.source);
      const tgt = nodeMap.get(link.target);
      if (!src || !tgt) return null;
      return (
        <FlowRow
          key={`${link.source}-${link.target}-${idx}`}
          sourceName={src.name}
          targetName={tgt.name}
          value={link.value}
          maxValue={maxValue}
          sourceType={src.type}
          targetType={tgt.type}
          currency={currency}
        />
      );
    });

  return (
    <Box>
      {/* Column headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 160px',
          gap: 1,
          px: 1,
          mb: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', pr: 1, fontWeight: 700 }}>
          SOURCE
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 700 }}>
          FLOW
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1, fontWeight: 700 }}>
          DESTINATION
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
          borderRadius: 2,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          py: 1,
        }}
      >
        {level1Links.length > 0 && (
          <>
            {sectionHeader('Income → Categories')}
            <Stack spacing={0}>{renderLinks(level1Links)}</Stack>
          </>
        )}

        {level2Links.length > 0 && (
          <>
            {sectionHeader('Categories → Merchants')}
            <Stack spacing={0}>{renderLinks(level2Links)}</Stack>
          </>
        )}
      </Box>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mt: 1.5, px: 1, flexWrap: 'wrap', gap: 1 }}>
        {[
          { label: 'Income', color: TYPE_COLORS.income },
          { label: 'Category', color: TYPE_COLORS.category },
          { label: 'Merchant', color: TYPE_COLORS.merchant },
        ].map(({ label, color }) => (
          <Stack key={label} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
